import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faPaperPlane,
  faCheckCircle,
  faExclamationCircle,
  faCalendarCheck,
  faArrowRight,
  faCommentDots,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import BackToTopButton from "./BackToTopButton";
import { useBooking } from "../context/BookingContext";

const ContactForm = () => {
  const { openBooking } = useBooking();
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [barVisible, setBarVisible] = useState(false);
  const [barDismissed, setBarDismissed] = useState(false);

  /* Show sticky bar after scrolling 40% of the page */
  useEffect(() => {
    const onScroll = () => {
      const pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (pct > 0.4) setBarVisible(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const res  = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus("success");
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setStatus("idle"), 3000);
      } else {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 3000);
      }
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <>
      <section
        id="contact"
        className="py-20 md:py-28 lg:py-32 px-4 sm:px-5 md:px-10 lg:px-12 bg-background relative overflow-hidden"
      >
        <div className="max-w-5xl mx-auto relative z-10">

          {/* Section header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary border border-border
                            text-xs font-semibold mb-6 tracking-widest uppercase text-muted-foreground">
              Get Started
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4 font-heading tracking-tight">
              Ready to scale your product<br className="hidden sm:block" /> or automate your operations?
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Let's discuss your technical requirements and build a high-performance solution.
            </p>
          </div>

          {/* ── Two-column layout ── */}
          <div className="flex flex-col lg:flex-row gap-6 sm:gap-8 lg:gap-10">

            {/* Left column: direct contact + calendar */}
            <div className="flex-1 flex flex-col gap-5 sm:gap-6 min-w-0">

              {/* Direct contact card */}
              <div className="bg-card border border-border rounded-2xl sm:rounded-3xl p-5 sm:p-7 min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-foreground mb-5 font-heading tracking-tight">
                  Direct Contact
                </h3>
                <ul className="space-y-4 sm:space-y-5">
                  {[
                    { icon: faEnvelope,  label: "Email",    href: "mailto:akashzaheer786@gmail.com",               text: "akashzaheer786@gmail.com"   },
                    { icon: faGithub,    label: "GitHub",   href: "https://github.com/AkashZaheer045",              text: "AkashZaheer045",             blank: true },
                    { icon: faLinkedin, label: "LinkedIn",  href: "https://www.linkedin.com/in/muhammadakashzaheer/", text: "Muhammad Akash Zaheer",     blank: true },
                  ].map(({ icon, label, href, text, blank }) => (
                    <li key={label} className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                        <FontAwesomeIcon icon={icon} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
                          {label}
                        </div>
                        <a
                          href={href}
                          target={blank ? "_blank" : undefined}
                          rel={blank ? "noreferrer" : undefined}
                          className="hover:text-primary transition-colors font-medium text-sm break-all sm:break-words"
                        >
                          {text}
                        </a>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Book a call card */}
              <div className="bg-card border border-border border-dashed rounded-2xl sm:rounded-3xl
                              p-5 sm:p-7 flex flex-col items-center text-center min-h-[152px]
                              hover:shadow-soft transition-shadow">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary text-xl mb-3">
                  <FontAwesomeIcon icon={faCalendarCheck} />
                </div>
                <h4 className="font-heading font-bold text-foreground text-base mb-1.5 tracking-tight">
                  Book a Strategy Call
                </h4>
                <p className="text-muted-foreground text-sm mb-4 max-w-xs leading-relaxed">
                  Free 30-minute call to discuss your project requirements.
                </p>
                <button
                  onClick={openBooking}
                  id="contact-book-call"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-primary/80
                             text-primary-foreground rounded-full font-bold text-sm shadow-glow
                             hover:shadow-glow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  Schedule Call
                  <FontAwesomeIcon icon={faArrowRight} className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Right column: 3-field form */}
            <form
              className="flex-1 bg-card border border-border rounded-2xl sm:rounded-3xl
                         p-5 sm:p-7 md:p-9 flex flex-col gap-5 hover:shadow-soft transition-shadow min-w-0"
              onSubmit={handleSubmit}
              id="contact-form"
            >
              <h3 className="text-lg sm:text-xl font-bold text-foreground font-heading tracking-tight">
                Send a Message
              </h3>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-foreground text-sm font-semibold mb-2">
                  Name
                </label>
                <input
                  type="text" id="name" name="name"
                  className="w-full px-4 py-3.5 rounded-xl bg-secondary/50 border border-border
                             text-foreground placeholder:text-muted-foreground/60
                             focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary
                             transition-all min-w-0"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-foreground text-sm font-semibold mb-2">
                  Email
                </label>
                <input
                  type="email" id="email" name="email"
                  className="w-full px-4 py-3.5 rounded-xl bg-secondary/50 border border-border
                             text-foreground placeholder:text-muted-foreground/60
                             focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary
                             transition-all min-w-0"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Project overview */}
              <div>
                <label htmlFor="message" className="block text-foreground text-sm font-semibold mb-2">
                  Project Overview
                </label>
                <textarea
                  id="message" name="message" rows="5"
                  className="w-full px-4 py-3.5 rounded-xl bg-secondary/50 border border-border
                             text-foreground placeholder:text-muted-foreground/60
                             focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary
                             transition-all resize-none min-w-0"
                  placeholder="Describe your project, timeline, and technical requirements..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={status === "submitting" || status === "success"}
                id="contact-submit"
                className={`w-full py-4 rounded-xl font-bold text-base flex items-center justify-center gap-2
                            transition-all duration-300 ${
                  status === "success"
                    ? "bg-emerald-600 text-white cursor-default"
                    : status === "error"
                    ? "bg-red-600 text-white cursor-default"
                    : "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:shadow-glow hover:-translate-y-0.5 active:scale-[0.98]"
                }`}
              >
                {status === "submitting" ? (
                  <span>Sending…</span>
                ) : status === "success" ? (
                  <><FontAwesomeIcon icon={faCheckCircle} /> Sent Successfully</>
                ) : status === "error" ? (
                  <><FontAwesomeIcon icon={faExclamationCircle} /> Failed to Send</>
                ) : (
                  <><FontAwesomeIcon icon={faPaperPlane} /> Send Message</>
                )}
              </button>
            </form>
          </div>

          {/* Footer */}
          <div className="mt-16 sm:mt-20 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
              <div>
                <p className="text-foreground font-heading font-bold text-lg tracking-tight mb-1">
                  Muhammad Akash Zaheer
                </p>
                <p className="text-muted-foreground text-sm font-medium">
                  Full-Stack Engineer &amp; AI Systems Consultant
                </p>
              </div>
              <div className="flex items-center gap-2">
                {[
                  { icon: faGithub,   href: "https://github.com/AkashZaheer045",                      label: "GitHub"   },
                  { icon: faLinkedin, href: "https://www.linkedin.com/in/muhammadakashzaheer/",        label: "LinkedIn" },
                  { icon: faEnvelope, href: "mailto:akashzaheer786@gmail.com",                         label: "Email"    },
                ].map(({ icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noreferrer" : undefined}
                    aria-label={label}
                    className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center
                               text-foreground hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                  >
                    <FontAwesomeIcon icon={icon} />
                  </a>
                ))}
              </div>
            </div>
            <div className="text-center mt-8 pt-6 border-t border-border/50">
              <p className="text-muted-foreground text-sm font-medium">
                © {new Date().getFullYear()} Muhammad Akash Zaheer. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        <BackToTopButton />
      </section>

      {/* ── Sticky CTA bar ── */}
      {barVisible && !barDismissed && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-between gap-3
                     px-4 sm:px-6 py-3 sm:py-4
                     bg-background/95 backdrop-blur-xl border-t border-border
                     shadow-elevated"
          role="complementary"
          aria-label="Sticky call to action"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
              <FontAwesomeIcon icon={faCommentDots} className="text-sm" />
            </div>
            <p className="text-sm font-medium text-foreground hidden sm:block truncate">
              Need a technical partner?{" "}
              <span className="text-muted-foreground">Let's discuss your product.</span>
            </p>
            <p className="text-sm font-medium text-foreground sm:hidden">
              Need a technical partner?
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={openBooking}
              className="px-4 sm:px-5 py-2 bg-gradient-to-r from-primary to-primary/80
                         text-primary-foreground rounded-full font-bold text-xs sm:text-sm
                         hover:shadow-glow hover:-translate-y-0.5 transition-all duration-300
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                         whitespace-nowrap cursor-pointer"
            >
              Book Strategy Call
            </button>
            <button
              onClick={() => setBarDismissed(true)}
              aria-label="Dismiss"
              className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground
                         hover:text-foreground hover:bg-secondary transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactForm;
